/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import ApiService from "./ApiService";
import Attributes from "../model/Attributes";
import AttributeValues from "../model/AttributeValues";
import SkillStats from "../model/SkillStats";
import CharacterService from "./CharacterService";
import Section from "../model/Section";
import Class from "../model/Class";
import { ClassDetails } from "../model/ClassDetails";
import Abilities from "../model/Abilities";

interface Option {
    label: string,
    options: string[]
};

interface ClassOpen5e extends Class {
    archetypes: Class[];
}

class ClassService {
    private static baseMetaCaster = {
        name: 'Meta Caster',
        desc: '',
        slug: 'meta_caster',
        base: { name: '' },
        subtypes_name: '',
        hit_dice: '',
        prof_armor: '',
        prof_weapons: '',
        prof_tools: '',
        prof_saving_throws: '',
        prof_skills: '',
        equipment: '',
        table: '',
        sections: [],
        getAsiLevels: (level: number) => [],
        document__slug: 'meta_caster',
        document__title: '',
        document__license_url: '',
        document__url: ''
    };

    static metaCasterWisdom = {
        ...ClassService.baseMetaCaster,
        name: 'Inate Caster - Wis',
        slug: 'inate_caster_wis',
        spellcasting_ability: 'wisdom'
    };

    static metaCasterIntelligence = {
        ...ClassService.baseMetaCaster,
        name: 'Inate Caster - Int',
        slug: 'inate_caster_int',
        spellcasting_ability: 'intelligence'
    };

    static metaCasterCharisma = {
        ...ClassService.baseMetaCaster,
        name: 'Inate Caster - Cha',
        slug: 'inate_caster_cha',
        spellcasting_ability: 'charisma'
    };

    static expandArchetypes(classes: ClassOpen5e[]): Class[] {
        return classes.reduce((acc: Class[], pcClass: ClassOpen5e) => {
            const { archetypes, ...parentAttributes } = pcClass;
            acc.push(parentAttributes);
            archetypes.forEach(archetype => {
                // Deep clone parent attributes to avoid mutations
                const parentClone = JSON.parse(JSON.stringify(parentAttributes));

                // Function to combine strings with a check to avoid duplicating identical content
                const describeArchetype = (name: string, desc: string) => {
                    return `### ${name}\n${desc}`;
                };

                const newArchetype: Class = {
                    ...parentClone,            // Start with a clone of the parent attributes
                    ...archetype,              // Overwrite with archetype-specific attributes
                    base: parentClone,
                    archetypeName: archetype.name,
                    slug: archetype.slug,
                    desc: describeArchetype(archetype.name, archetype.desc)
                };

                acc.push(newArchetype);
            });
            return acc;
        }, []);
    }
    static getLevelString(levelInt: number): string {
        if (levelInt == 1) return '1st';
        if (levelInt == 2) return '2nd';
        if (levelInt == 3) return '3rd';
        return levelInt + 'th';
    }
    static getLevelAbility(classInfo: Class, classLevel: number, ability: string): string {
        if (classInfo) {
            if (classLevel > 20) {
                classLevel = 20;
            }
            const levelStr = this.getLevelString(classLevel);
            let tableRow = CharacterService.getTableRow(classInfo.table, levelStr);
            for (const tableCell of tableRow || []) {
                if (tableCell.name.toLowerCase() === ability.toLowerCase()) {
                    return tableCell.desc;
                }
            }
            for (const section of classInfo.sections || []) {
                if (section.desc.includes('--|--')) {
                    tableRow = CharacterService.getTableRow(section.desc, levelStr);
                    if (tableRow) {
                        break;
                    }
                }
            }
            for (const tableCell of tableRow || []) {
                if (tableCell.name.toLowerCase() === ability.toLowerCase()) {
                    return tableCell.desc;
                }
            }
        }
        return '';
    }

    static getSpellCastingAttribute(classDetails: ClassDetails): string {
        const fullClassName = (classDetails.classInfo?.name || '') + '/' + (classDetails.archetype?.name || '');
        return ClassService.getSpellCastingAttributeByClassName(fullClassName) ||
            ClassService.getSpellCastingAttributeByClassName(classDetails.classInfo?.name || '');

    }
    static getSpellCastingAttributeByClassName(fullClassName: string): string {
        switch (fullClassName.toLowerCase()) {
            case 'bard':
            case 'paladin':
            case 'sorcerer':
            case 'warlock':
            case 'inate caster - cha':
                return 'Charisma';

            case 'druid':
            case 'cleric':
            case 'ranger':
            case 'rogue/soulspy':
            case 'rogue/underfoot':
            case 'inate caster - wis':
                return 'Wisdom';

            case 'fighter/arcane warrior':
            case 'rogue/eldritch trickster':
            case 'rogue/smuggler':
            case 'wizard':
            case 'inate caster - int':
                return 'Intelligence';
        }
        return '';
    }

    static getSpellsKnownForClassLevel(classInfo: Class | null, classLevel: number, attributeValues: AttributeValues): number {
        if (classInfo == null) {
            return 0;
        }
        if (classInfo.name.toLowerCase().startsWith('inate caster') || classLevel > 20) {
            return 99;
        }
        if (classInfo.name.toLowerCase() === 'wizard') {
            // This is really the count of prepared spells.
            return Math.max(1, (classLevel + Attributes.getBonus(attributeValues.intelligence)));
        }
        if ((classInfo.name.toLowerCase() === 'cleric') ||
            (classInfo.name.toLowerCase() === 'druid')) {
            return Math.max(1, (classLevel + Attributes.getBonus(attributeValues.wisdom)));
        }
        if (classInfo.name.toLowerCase() === 'paladin') {
            return Math.max(1, (Math.floor(classLevel / 2) + Attributes.getBonus(attributeValues.charisma)));
        }
        if ((classInfo.name.toLowerCase() === 'smuggler') && (classLevel >= 7)) {
            if (classLevel >= 13) {
                return 2;
            }
            return 1;
        }
        const spellsKnownAtLevel = this.getLevelAbility(classInfo, classLevel, 'Spells Known');
        return parseInt(spellsKnownAtLevel) || 0;
    }

    static getHighestSpellsLevelKnownForClassLevel(classInfo: Class | null, classLevel: number): number {
        if (!classInfo) {
            return 0;
        }
        if (classInfo.name.toLowerCase().startsWith('inate caster')) {
            return 9;
        }
        if (classInfo.name.toLowerCase() === 'warlock') {
            const spellsKnownAtSpellLevel = this.getLevelAbility(classInfo, classLevel, 'Slot Level');
            if (spellsKnownAtSpellLevel) {
                return parseInt(spellsKnownAtSpellLevel);
            }
        }
        for (let spellLevel = 9; spellLevel > 0; spellLevel--) {
            const spellLevelStr = this.getLevelString(spellLevel);
            const spellsKnownAtSpellLevel = this.getLevelAbility(classInfo, classLevel, spellLevelStr);
            if (spellsKnownAtSpellLevel) {
                const spells = parseInt(spellsKnownAtSpellLevel);
                if (spells && spells > 0) {
                    return spellLevel;
                }
            }
        }
        return 0;
    }

    static getCantripsKnonwn(classInfo: Class, classLevel: number): number {
        if ((classInfo.name.toLowerCase() === 'smuggler') && (classLevel >= 3)) {
            return 2;
        }
        const cantripsKnown = this.getLevelAbility(classInfo, classLevel, 'Cantrips Known') ||
            this.getLevelAbility(classInfo, classLevel, 'Known Cantrips');
        return parseInt(cantripsKnown) || 0;
    }

    static getSpellsKnowableBySpellLevelForClassLevel(classInfo: Class, classLevel: number, attributeValues: AttributeValues): number[] {
        if (classLevel < 1) {
            return [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        }
        if (classInfo.name.toLowerCase().startsWith('inate caster') || classLevel > 20) {
            const maxLevel = ClassService.getHighestSpellsLevelKnownForClassLevel(classInfo, classLevel);
            const results = [];
            for (let spellLevel = 0; spellLevel <= maxLevel; spellLevel++) {
                results[spellLevel] = 9;
            }
            return results;
        }
        if (classInfo.name.toLowerCase() === 'smuggler') {
            if (classLevel >= 13) {
                return [2, 1, 1, 0, 0, 0, 0, 0, 0, 0];
            }
            if (classLevel >= 7) {
                return [2, 1, 0, 0, 0, 0, 0, 0, 0, 0];
            }
            if (classLevel >= 3) {
                return [2, 0, 0, 0, 0, 0, 0, 0, 0, 0];
            }
            return [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        }
        const cantripsKnown = this.getCantripsKnonwn(classInfo, classLevel);
        if (classInfo.name.toLowerCase() === 'wizard') {
            const spells = [];
            spells.push(cantripsKnown)
            for (let spellLevel = 1; spellLevel <= 9; spellLevel++) {
                if (spellLevel == this.getHighestSpellsLevelKnownForClassLevel(classInfo, classLevel)) {
                    spells.push(Attributes.getBonus(attributeValues.intelligence) + classLevel);
                } else {
                    spells.push(0);
                }
            }
            return spells;
        }
        if ((classInfo.name.toLowerCase() === 'cleric') ||
            (classInfo.name.toLowerCase() === 'druid') ||
            (classInfo.name.toLowerCase() === 'paladin')) {
            // clerics really know all the spells, but this list will show the prepared spells instead:
            const spells = this.getSpellsKnownForClassLevel(classInfo, classLevel, attributeValues);
            const spellsByLevel = [];
            spellsByLevel.push(cantripsKnown);
            const highestLevel = this.getHighestSpellsLevelKnownForClassLevel(classInfo, classLevel);
            for (let lvl = 1; lvl <= highestLevel; lvl++) {
                spellsByLevel.push(spells);
            }
            while (spellsByLevel.length < 10) {
                spellsByLevel.push(0);
            }
            return spellsByLevel;
        }
        if (classLevel == 1) {
            const spellsKnownBySpellLevel: number[] = [];
            spellsKnownBySpellLevel.push(cantripsKnown);
            spellsKnownBySpellLevel.push(this.getSpellsKnownForClassLevel(classInfo, classLevel, attributeValues));
            for (let spellLevel = 2; spellLevel <= 9; spellLevel++) {
                spellsKnownBySpellLevel.push(0);
            }
            return spellsKnownBySpellLevel;
        }
        const spellsKnownBySpellLevel = this.getSpellsKnowableBySpellLevelForClassLevel(classInfo, classLevel - 1, attributeValues);
        spellsKnownBySpellLevel[0] = cantripsKnown;
        const spellsKnown = this.getSpellsKnownForClassLevel(classInfo, classLevel, attributeValues);
        const spellsKnownPriorLevel = this.getSpellsKnownForClassLevel(classInfo, classLevel - 1, attributeValues);
        const newSpells = spellsKnown - spellsKnownPriorLevel;
        const highestSpellsLevelKnown = this.getHighestSpellsLevelKnownForClassLevel(classInfo, classLevel);
        spellsKnownBySpellLevel[highestSpellsLevelKnown] = spellsKnownBySpellLevel[highestSpellsLevelKnown] + newSpells;
        // remove one of the lowest level spells, and add it to the highest level

        for (let spellLevel = 1; spellLevel <= 9; spellLevel++) {
            if (spellsKnownBySpellLevel[spellLevel] > 0) {
                spellsKnownBySpellLevel[spellLevel] = spellsKnownBySpellLevel[spellLevel] - 1;
                spellsKnownBySpellLevel[highestSpellsLevelKnown] = spellsKnownBySpellLevel[highestSpellsLevelKnown] + 1;
                break;
            }
        }
        return spellsKnownBySpellLevel;
    }

    static findMinLevel(text: string): number | undefined {
        const parts = text.split(' level,');
        if (parts.length > 0) {
            const firstPart = parts[0];
            const startWords = ['At ', 'By ', 'Beginning at ', 'Starting at ', 'When you reach ', 'Upon reaching ', 'Also at ', 'Beginning when you reach '];
            for (const startWord of startWords) {
                if (firstPart.startsWith(startWord)) {
                    const numberPart = firstPart.substr(startWord.length).trim();
                    const result = numberPart.match(/\d+/);  // This regex matches the sequence of digits
                    return result ? parseInt(result[0], 10) : undefined; // Parse the match as an integer
                }
            }
        }

        const levelMatch = text.match(/^\*Prerequisite: (\d+)th level/);
        if (levelMatch) {
            return parseInt(levelMatch[1], 10);
        }

        return 0;
    }

    static checkAttributeRequirement(attributeName: string, attributeVals: AttributeValues): boolean {
        for (const attr of attributeName.split(' or ')) {
            const attrLevel = attributeVals[attr.toLowerCase() as keyof AttributeValues];
            if (attrLevel >= 13) {
                return true;
            }
        }
        return false;
    }

    static getSecondClassArmorProficiencies(classInfo: Class): string {
        switch (classInfo?.name) {
            case 'Barbarian': return 'Shields';
            case 'Bard':      return 'Light Armor';
            case 'Cleric':    return 'Light Armor, Medium Armor, Shields';
            case 'Druid':     return 'Light Armor, Medium Armor, Shields (druids will not wear armor or use shields made of metal)';
            case 'Fighter':   return 'Light Armor, Medium Armor, Shields';
            case 'Monk':      return '';
            case 'Paladin':   return 'Light Armor, Medium Armor, Shields';
            case 'Ranger':    return 'Light Armor, Medium Armor, Shields';
            case 'Rogue':     return 'Light Armor';
            case 'Sorcerer':  return '';
            case 'Warlock':   return 'Light Armor';
            case 'Wizard':    return '';
        }
        return '';
    }

    static getSecondClassWeaponProficiencies(classInfo: Class): string {
        switch (classInfo?.name) {
            case 'Barbarian': return 'Simple Weapons, Martial Weapons';
            case 'Bard':      return '';
            case 'Cleric':    return '';
            case 'Druid':     return '';
            case 'Fighter':   return 'Simple Weapons, Martial Weapons';
            case 'Monk':      return 'Simple weapons, Shortsword';
            case 'Paladin':   return 'Simple Weapons, Martial Weapons';
            case 'Ranger':    return 'Simple Weapons, Martial Weapons';
            case 'Rogue':     return '';
            case 'Sorcerer':  return '';
            case 'Warlock':   return 'Simple Weapons';
            case 'Wizard':    return '';
        }
        return '';
    }

    static getSecondClassSkillProficiencies(classInfo: Class): string {
        switch (classInfo?.name) {
            case 'Barbarian': return '';
            case 'Bard':      return 'One skill of your choice, one Musical Instrument of your choice';
            case 'Cleric':    return '';
            case 'Druid':     return '';
            case 'Fighter':   return '';
            case 'Monk':      return '';
            case 'Paladin':   return '';
            case 'Ranger':    return 'Choose one from Animal Handling, Athletics, Insight, Investigation, Nature, Perception, Stealth, and Survival';
            case 'Rogue':     return 'Thieves\' Tools, plus choose one from Acrobatics, Athletics, Deception, Insight, Intimidation, Investigation, Perception, Performance, Persuasion, Sleight of Hand, and Stealth';
            case 'Sorcerer':  return '';
            case 'Warlock':   return '';
            case 'Wizard':    return '';
        }
        return '';
    }

    static checkMultiClassRequirement(className: string, attributeVals: AttributeValues): Set<string> {
        let attrName = '';
        if (className === 'Barbarian')     attrName = 'Strength';
        else if (className === 'Bard')     attrName = 'Charisma';
        else if (className === 'Cleric')   attrName = 'Wisdom';
        else if (className === 'Druid')    attrName = 'Wisdom';
        else if (className === 'Fighter')  attrName = 'Strength or Dexterity';
        else if (className === 'Monk')     attrName = 'Dexterity and Wisdom';
        else if (className === 'Paladin')  attrName = 'Strength and Charisma';
        else if (className === 'Ranger')   attrName = 'Dexterity and Wisdom';
        else if (className === 'Rogue')    attrName = 'Dexterity';
        else if (className === 'Sorcerer') attrName = 'Charisma';
        else if (className === 'Warlock')  attrName = 'Charisma';
        else if (className === 'Wizard')   attrName = 'Intelligence';
        const errors = new Set<string>();
        for (const attr of attrName.split(' and ')) {
            if (!ClassService.checkAttributeRequirement(attr, attributeVals)) {
                errors.add(attr);
            }
        }
        return errors;
    }

    static parseTextToSections(text: string): Section[] {
        const sectStrs: string[] = text.split(/\n *(?=(?:[#_\*]{2,}|> \#{3,}))/); // Split by lines starting with '##', '###', or '####', or '**', '***', or '> ###'...
        const sectsOrNulls: (Section | null)[] = sectStrs.map(sect => {
            const options: string[] = [];
            const selectedOption = '';
            const children: Section[] = [];

            let depth = 0;
            if (sect.startsWith('> ')) {
                sect = sect.substring(2).replace(/\n> /g, '\n');
                depth++;
            }
            if (sect.startsWith('##') || sect.includes('--|--')) {
                let [name, ...descriptionParts] = sect.trim().split('\n');
                const desc = descriptionParts.join('\n').trim(); // Join back the description parts split earlier
                name = name.trim();
                while (name.startsWith('#')) {
                    depth++;
                    name = name.substr(1);
                }
                while ((name.startsWith('*') && name.endsWith('*')) || (name.startsWith('_') && name.endsWith('_'))) {
                    name = name.substr(1, name.length - 2);
                }
                name = name.trim();

                return new Section(name.trim(), desc, depth, ClassService.findMinLevel(desc), children, null, options, selectedOption);
            }
            if (sect.startsWith('**')) {
                const array = sect.trim().split(/[\*_]+/);
                return new Section(
                    '',//array[1].trim(), // Name is taken directly with its hashes
                    '>' + sect, // array[2]
                    depth + 3,
                    ClassService.findMinLevel(array[2]),
                    children, null, options, selectedOption
                );
            }
            return null;
        });
        return sectsOrNulls.filter(o => o != null) as Section[];
    };

    // parse all the class descriptions into sections:
    static fixClassErrors(pcClass: Class): void {
        if (!pcClass.desc) return;
        if (pcClass.name === "Soulspy" || pcClass.name === 'Underfoot') {
            // 'Spells Unknown' and wrong column count in divider row
            pcClass.desc = pcClass.desc.replace("| Rouge Level  | Cantrips Known | Spells Unknown | 1st | 2nd | 3rd | 4th |",
                                                "| Rouge Level  | Cantrips Known | Spells known | 1st | 2nd | 3rd | 4th |");
            pcClass.desc = pcClass.desc.replace("|--------------|----------------|----------------------|-----|-----|-----| ",
                                                "|--------------|----------------|--------------|-----|-----|-----|-----| ");
        }

        // fix issue of headers not being on their own line:
        pcClass.desc = pcClass.desc.replace(/([\.a-mo-z] *)##/g, '$1\n###');

        if (pcClass.name === 'Blood Domain') {
            // remove this table, because it's not formatted well, doesn't list its minimum level, and only causes confusion
            pcClass.desc = pcClass.desc.replace(
                " **Health State Examples**\n"+
                " | 100%    | Untouched            | \n"+
                " | 99%-50% | Injured              | \n"+
                " | 49%-1%  | Heavily Wounded      | \n"+
                " | 0%      | Unconscious or Dying | \n"+
                " | –       | Dead                 |\n\n",
                "");
        }
        if (pcClass.name === 'Bard') {
            // Bard's column headers are switched
            pcClass.table = pcClass.table?.replace('Spells Known | Cantrips Known', 'Cantrips Known | Spells Known');
        }
        // Keep the naming of 'Spells Known' consistent:
        pcClass.table = pcClass.table?.replace('| Known Spells |', '| Spells Known |');
    }

    static parseSections(pcClass: Class): void {
        if (!pcClass.desc) return;

        pcClass.desc = pcClass.desc.replace(/ \n \n/g, '\n');
        const sections = ClassService.parseTextToSections(pcClass.desc);
        pcClass.sections = [];
        let lastSection = null;
        for (const section of sections) {
            if (section.name === 'Your Pact Boon') {
                section.depth--;
            }
            if (lastSection && section.depth === lastSection.depth + 1) {
                lastSection.children.push(section);
                if ((lastSection.name === 'Fighting Style') ||
                    (lastSection.name === 'Pact Boon') ||
                    (lastSection.name === 'Eldritch Invocations') ||
                    (lastSection.name === 'Metamagic') ||
                    (lastSection.name === 'Wyrmling Blessing')) {
                    section.parentName = lastSection.name;
                    lastSection.options.push(section.name);
                }
            } else {
                pcClass.sections.push(section);
                lastSection = section;
            }
        }
    };

    static addAsiLevelToClass(pcClass: Class): void {
        if (!pcClass) return;
        pcClass.getAsiLevels = (level: number): number[] => {
            const rows = pcClass.table.split('\n');
            const headers = rows[0].split('|').map((o) => o.trim());
            const featureColumnIndex = headers.indexOf('Features');
            const asiLevels: number[] = [];
            for (let i = 0; (i < level) && (rows.length > (2 + i)); i++) {
                const featureAtLevel = rows[2 + i].split('|')[featureColumnIndex];
                if (featureAtLevel.includes('Ability Score Improvement')) {
                    asiLevels.push(i + 1);
                }
            }
            return asiLevels;
        }
    };

    static parseLanguages(classInfo: Class | null, languageOptions: Option[], languagesSelected: string[]): void {
        classInfo?.sections?.forEach(section => {
            if (section.name.toLowerCase() === 'thieves\' cant') {
                const lang = 'Thieves\' cant';
                languageOptions.push({ label: classInfo.name, options: [lang] });
                languagesSelected.push(lang);
            }
            if (section.name.toLowerCase() === 'druidic') {
                const lang = 'Druidic';
                languageOptions.push({ label: classInfo.name, options: [lang] });
                languagesSelected.push(lang);
            }
        });
    }

    static parseArmor(classInfo: Class | null, armorOptions: Option[], armorSelected: string[], isPrimaryClass: boolean): void {
        let prof_armor = classInfo?.prof_armor;
        if (!isPrimaryClass && classInfo) {
            prof_armor = this.getSecondClassArmorProficiencies(classInfo);
        }
        const isDruid = prof_armor?.includes('(druids will not wear armor or use shields made of metal)');
        if (isDruid) {
            prof_armor = prof_armor?.replace('(druids will not wear armor or use shields made of metal)', '').trim();
        }
        const armors = prof_armor?.toLocaleLowerCase().split(',').map(ar => ar.trim()) || [];
        for (const armor of ['Light Armor', 'Medium Armor', 'Heavy Armor', 'All Armor', 'Shields']) {
            if (armors.includes(armor.toLocaleLowerCase())) {
                if (isDruid) {
                    armorOptions.push({ label: classInfo?.name || 'Class', options: [armor + ' (not made of metal)'] });
                } else {
                    armorOptions.push({ label: classInfo?.name || 'Class', options: [armor] });
                }
                armorSelected.push(armor);
            }
        }
    }

    static parseWeapons(classInfo: Class | null, weaponsOptions: Option[], weaponsSelected: string[], isPrimaryClass: boolean): void {
        let prof_weapons = classInfo?.prof_weapons;
        if (!isPrimaryClass && classInfo) {
            prof_weapons = this.getSecondClassWeaponProficiencies(classInfo);
        }
        const weapons = prof_weapons?.toLocaleLowerCase().split(',').map(ar => ar.trim()) || [];
        for (const weapon of [...Abilities.getGearNames(), 'Simple Weapons', 'Martial Weapons']) {
            if (weapons.includes(weapon.toLocaleLowerCase())) {
                weaponsOptions.push({ label: classInfo?.name || 'Class', options: [weapon] });
                weaponsSelected.push(weapon);
            }
        }
    }

    static parseEquipment(classInfo: Class | null, equipmentOptions: Option[], equipmentSelected: string[], isPrimaryClass: boolean): void {
        if (isPrimaryClass && classInfo) {
            // secondary classes dont get equipment
            const eqOptions: string[] = classInfo.equipment.split(/[\n]\s?/);
            eqOptions.forEach(equipment => {
                let options: string[] = equipment.split(/\*?\(\*?[a-z]\*?\)\*?/i);
                options = options.map(o => {
                    o = o.trim();
                    if (o.startsWith('*')) {
                        o = o.substring(1).trim();
                    }
                    if (o.endsWith(' or')) {
                        o = o.substring(0, o.length - 3).trim();
                    }
                    if (o.endsWith(',')) {
                        o = o.substring(0, o.length - 1).trim();
                    }
                    if (o.toLocaleLowerCase() === 'you start with the following equipment, in addition to the equipment granted by your background:') {
                        return '';
                    }
                    return o;
                });
                // remove blank/empty rows:
                options = options.filter(o => !!o);
                if (options.length > 0) {
                    if (options.length === 1) {
                        const singleOption = options[0];
                        if (singleOption.includes(' and ')) {
                            const multiOptions = singleOption.replace(' and ', ',').replace(',,', ',').split(',');
                            multiOptions.forEach(opt => {
                                equipmentOptions.push({ label: classInfo.name, options: [opt] });
                                equipmentSelected.push(opt);
                            });
                        } else {
                            equipmentOptions.push({ label: classInfo.name, options: options });
                            equipmentSelected.push(singleOption);
                        }
                    } else {
                        equipmentOptions.push({ label: classInfo.name, options: options });
                        equipmentSelected.push('')
                    }
                }
            });
        }
        // •	(a) a greataxe or (b) any martial melee weapon
        // •	(a) two handaxes or (b) any simple weapon
        // •	An explorer's pack and four javelins
        // -----------
        // •	(a) a rapier, (b) a longsword, or (c) any simple weapon
        // •	(a) a diplomat's pack or (b) an entertainer's pack
        // •	(a) a lute or (b) any other musical instrument
        // •	Leather armor and a dagger
        // -----------
        // •	(a) a mace or (b) a warhammer (if proficient)
        // •	(a) scale mail, (b) leather armor, or (c) chain mail (if proficient)
        // •	(a) a light crossbow and 20 bolts or (b) any simple weapon
        // •	(a) a priest's pack or (b) an explorer's pack
        // •	A shield and a holy symbol
        // -----------
        // •	(a) a wooden shield or (b) any simple weapon
        // •	(a) a scimitar or (b) any simple melee weapon
        // •	Leather armor, an explorer's pack, and a druidic focus
        // -----------
        // •	(a) chain mail or (b) leather armor, longbow, and 20 arrows
        // •	(a) a martial weapon and a shield or (b) two martial weapons
        // •	(a) a light crossbow and 20 bolts or (b) two handaxes
        // •	(a) a dungeoneer's pack or (b) an explorer's pack
        // -----------
        // •	(a) a shortsword or (b) any simple weapon
        // •	(a) a dungeoneer's pack or (b) an explorer's pack
        // •	10 darts
        // -----------
        // •	(a) a martial weapon and a shield or (b) two martial weapons
        // •	(a) five javelins or (b) any simple melee weapon
        // •	(a) a priest's pack or (b) an explorer's pack
        // •	Chain mail and a holy symbol
        // -----------
        // •	(a) scale mail or (b) leather armor
        // •	(a) two shortswords or (b) two simple melee weapons
        // •	(a) a dungeoneer's pack or (b) an explorer's pack
        // •	A longbow and a quiver of 20 arrows
        // -----------
        // •	(a) a rapier or (b) a shortsword
        // •	(a) a shortbow and quiver of 20 arrows or (b) a shortsword
        // •	(a) a burglar's pack, (b) a dungeoneer's pack, or (c) an explorer's pack
        // •	(a) Leather armor, two daggers, and thieves' tools
        // -----------
        // •	(a) a light crossbow and 20 bolts or (b) any simple weapon
        // •	(a) a component pouch or (b) an arcane focus
        // •	(a) a dungeoneer's pack or (b) an explorer's pack
        // •	Two daggers
        // -----------
        // •	(a) a light crossbow and 20 bolts or (b) any simple weapon
        // •	(a) a component pouch or (b) an arcane focus
        // •	(a) a scholar's pack or (b) a dungeoneer's pack
        // •	Leather armor, any simple weapon, and two daggers
        // -----------
        // •	(a) a quarterstaff or (b) a dagger
        // •	(a) a component pouch or (b) an arcane focus
        // •	(a) a scholar's pack or (b) an explorer's pack
        // •	A spellbook



        // a greataxe or 
        // any martial melee weapon
        // two handaxes or 
        // any simple weapon
        // An explorer's pack and four javelins
        // -----------
        // a rapier, 
        // a longsword, or 
        // any simple weapon
        // a diplomat's pack or 
        // an entertainer's pack
        // a lute or 
        // any other musical instrument
        // Leather armor and a dagger
        // -----------
        // a mace or 
        // a warhammer (if proficient)
        // scale mail, 
        // leather armor, or 
        // chain mail (if proficient)
        // a light crossbow and 20 bolts or 
        // any simple weapon
        // a priest's pack or 
        // an explorer's pack
        // A shield and a holy symbol
        // -----------
        // a wooden shield or 
        // any simple weapon
        // a scimitar or 
        // any simple melee weapon
        // Leather armor, an explorer's pack, and a druidic focus
        // -----------
        // chain mail or 
        // leather armor, longbow, and 20 arrows
        // a martial weapon and a shield or 
        // two martial weapons
        // a light crossbow and 20 bolts or 
        // two handaxes
        // a dungeoneer's pack or 
        // an explorer's pack
        // -----------
        // a shortsword or 
        // any simple weapon
        // a dungeoneer's pack or 
        // an explorer's pack
        // 10 darts
        // -----------
        // a martial weapon and a shield or 
        // two martial weapons
        // five javelins or 
        // any simple melee weapon
        // a priest's pack or 
        // an explorer's pack
        // Chain mail and a holy symbol
        // -----------
        // scale mail or 
        // leather armor
        // two shortswords or 
        // two simple melee weapons
        // a dungeoneer's pack or 
        // an explorer's pack
        // A longbow and a quiver of 20 arrows
        // -----------
        // a rapier or 
        // a shortsword
        // a shortbow and quiver of 20 arrows or 
        // a shortsword
        // a burglar's pack, 
        // a dungeoneer's pack, or 
        // an explorer's pack
        // Leather armor, two daggers, and thieves' tools
        // -----------
        // a light crossbow and 20 bolts or 
        // any simple weapon
        // a component pouch or 
        // an arcane focus
        // a dungeoneer's pack or 
        // an explorer's pack
        // Two daggers
        // -----------
        // a light crossbow and 20 bolts or 
        // 
        // any simple weapon
        // a component pouch or 
        // 
        // an arcane focus
        // a scholar's pack or 
        // 
        // a dungeoneer's pack
        // Leather armor, any simple weapon, and two daggers
        // -----------
        // a quarterstaff or 
        // 
        // a dagger
        // a component pouch or 
        // 
        // an arcane focus
        // a scholar's pack or 
        // 
        // an explorer's pack
        // A spellbook

    }

    static parseOptions(label: string, input: string | undefined, options: Option[], selected: string[]): void {
        const optionsArray: string[][] = this.parseOptions2(input);
        for (const opts of optionsArray) {
            options.push({ label, options: opts })
            selected.push(opts[0]);
        }
    }

    static parseOptions2(input: string | undefined): string[][] {
        if (!input) {
            return [];
        }
        function cleanEnding(str: string) {
            return str
                .replace(/\s*(?:,\s*or|or|,)\s*$/i, '') // only at END of string
                .trim();
        }

        const result = input
            .split(/\r?\n/)                                   // split rows
            .map(line => line.replace(/^•\s*/, '').trim())    // strip bullet & spaces
            .filter(line => line.length)                      // drop empty lines
            .map(line => {
                const choices = line.match(/\([a-z]\)[^()]+/gi); // find "(a) ..." chunks
                if (choices) {
                    return choices.map(c => cleanEnding(c.replace(/\([a-z]\)/i, '').trim()));
                }
                return [cleanEnding(line)];
            });
        return result;
    }

    static parseSkills(classDetails: ClassDetails, skillOptions: Option[],
                       skillsSelected: string[], isPrimaryClass: boolean): void {
        /*
        "Choose any three",
        "Choose four from Acrobatics, Athletics, Deception, Insight, Intimidation, Investigation, Perception, Performance, Persuasion, Sleight of Hand, and Stealth",
        "Choose three from Animal Handling, Athletics, Insight, Investigation, Nature, Perception, Stealth, and Survival",
        "Choose two from Acrobatics, Athletics, History, Insight, Religion, and Stealth",
        "Choose two from Animal Handling, Athletics, Intimidation, Nature, Perception, and Survival",
        "Choose two from Arcana, Animal Handling, Insight, Medicine, Nature, Perception, Religion, and Survival",
        "Choose two from Arcana, Deception, Insight, Intimidation, Persuasion, and Religion",
        "Choose two from Arcana, History, Insight, Investigation, Medicine, and Religion",
        "Choose two from Athletics, Insight, Intimidation, Medicine, Persuasion, and Religion",
        "Choose two from History, Insight, Medicine, Persuasion, and Religion",
        "Choose two skills from Acrobatics, Animal, Handling, Athletics, History, Insight, Intimidation, Perception, and Survival",
        "Choose two skills from Arcana, Deception, History, Intimidation, Investigation, Nature, and Religion",
        */
        if (!classDetails.classInfo) {
            return;
        }

        if (isPrimaryClass) {
            CharacterService.splitSkills(classDetails.classInfo.prof_skills ?? '', classDetails.classInfo.name ?? '', skillOptions, skillsSelected);
        }
        // Go through the sections for the class and archetype, and parse them for skill proficiencies:
        if (classDetails.classLevel < 3) {
            classDetails.archetype = null;
        }
        for (const classInst of [classDetails.classInfo, classDetails.archetype].filter(o => o != null)) {
            for (const section of classInst?.sections || []) {
                if (section.minLevel && section.minLevel > classDetails.classLevel) {
                    continue;
                }
                const newOptions = []; // an array of arrays of strings
                let fullyMatched = true;
                if (section.desc === 'When you join the College of Lore at 3rd level, you gain proficiency with three skills of your choice.') {
                    newOptions.push([...SkillStats.getSkillsMap().keys()]);
                    newOptions.push([...SkillStats.getSkillsMap().keys()]);
                    newOptions.push([...SkillStats.getSkillsMap().keys()]);
                } else if (section.desc === 'When you join the College of Shadows at 3rd level, you gain proficiency in Stealth and in two other skills of your choice.') {
                    newOptions.push(['Stealth']);
                    newOptions.push([...SkillStats.getSkillsMap().keys()]);
                    newOptions.push([...SkillStats.getSkillsMap().keys()]);
                } else if (section.desc === 'When you join the College of Investigation at 3rd level, you gain proficiency in the Insight skill and in two of' +
                                            ' the following skills of your choice: Acrobatics, Deception, Investigation, Performance, Sleight of Hand, or Stealth.') {
                    newOptions.push(['Insight']);
                    newOptions.push(['Acrobatics', 'Deception', 'Investigation', 'Performance', 'Sleight of Hand', 'Stealth']);
                    newOptions.push(['Acrobatics', 'Deception', 'Investigation', 'Performance', 'Sleight of Hand', 'Stealth']);
                } else if (section.desc === 'At 3rd level, you gain proficiency in the Stealth skill. If you are already proficient in Stealth,' +
                                            ' you gain proficiency in another barbarian class skill of your choice.') {
                    let hasStealth = false;
                    for (const skillSelected of skillsSelected) {
                        if (skillSelected === 'Stealth') {
                            hasStealth = true;
                            break;
                        }
                    }
                    if (!hasStealth) {
                        newOptions.push(['Stealth']);
                    } else {
                        // barbarian class skills are: Animal Handling, Athletics, Intimidation, Nature, Perception, and Survival
                        newOptions.push(['Stealth', 'Animal Handling', 'Athletics', 'Intimidation', 'Nature', 'Perception', 'Survival']);
                    }
                } else if (section.desc === 'When you adopt this path at 3rd level, you gain proficiency in History and Performance.' +
                                            ' If you already have proficiency in one of these skills, your proficiency bonus is doubled for ability checks you make using that skill.') {
                    newOptions.push(['History', 'History' + SkillStats.EXPERT]);
                    newOptions.push(['Performance', 'Performance' + SkillStats.EXPERT]);
                    fullyMatched = false;
                } else if (section.desc === 'When you join the College of the Cat at 3rd level, you gain proficiency with the Acrobatics and Stealth skills and with thieves\' tools' +
                                            ' if you don\'t already have them. In addition, if you\'re proficient with a simple or martial melee weapon,' +
                                            ' you can use it as a spellcasting focus for your bard spells.') {
                    fullyMatched = false;
                    newOptions.push(['Acrobatics']);
                    newOptions.push(['Stealth']);
                    newOptions.push(['Thieves\' tools']);
                } else if (section.desc === 'When you choose this domain at 1st level, you take your place on the line between the two aspects of mercy: healing and killing.' +
                                            ' You gain proficiency in the Medicine skill and with the poisoner\'s kit. In addition, you gain proficiency with heavy armor and martial weapons.') {
                    fullyMatched = false;
                    newOptions.push(['Medicine']);
                    newOptions.push(['Poisoner\'s kit']);
                } else if (section.desc === 'When you choose this domain at 1st level, you gain proficiency with heavy armor and either cartographer\'s tools or navigator\'s tools' +
                                            ' (your choice). In addition, you gain proficiency in the Arcana skill.') {
                    fullyMatched = false;
                    newOptions.push(['Arcana']);
                    newOptions.push(['Cartographer\'s tools']);
                    newOptions.push(['Navigator\'s tools']);
                } else if (section.desc === 'When you choose this domain at 1st level, you learn the *poison spray* cantrip. In addition, you gain proficiency in the Deception skill,' +
                                            ' with a poisoner\'s kit, and with martial weapons that have the Finesse property.' +
                                            ' You can apply poison to a melee weapon or three pieces of ammunition as a bonus action.') {
                    fullyMatched = false;
                    newOptions.push(['Deception']);
                    newOptions.push(['Poisoner\'s kit']);
                } else if (section.desc === 'When you choose this domain at 1st level, you gain proficiency in the Stealth skill and darkvision out to a range of 60 feet.' +
                                            ' If you already have darkvision, its range increases by 30 feet. In addition, when you are in dim light or darkness,' +
                                            ' you can use a bonus action to Hide.') {
                    fullyMatched = false;
                    newOptions.push(['Stealth']);
                } else if (section.desc === 'When you choose this domain at 1st level, you learn the *mage hand* cantrip and gain proficiency in the Nature skill.' +
                                            ' When you cast *mage hand*, you can make the hand invisible, and you can control the hand as a bonus action.') {
                    fullyMatched = false;
                    newOptions.push(['Nature']);
                } else if (section.desc.startsWith('When you choose this circle at 2nd level, you gain proficiency in the Acrobatics or Stealth skill (your choice),' +
                                                   ' and you can speak and understand Bee Dance, a language shared by bees that involves flying in dance-like patterns.' +
                                                   ' Bees refuse to attack you, even with magical coercion.')) {
                    fullyMatched = false;
                    newOptions.push(['Acrobatics', 'Stealth']);
                } else if (section.desc === 'When you choose this archetype at 3rd level, you gain proficiency in the Insight, Medicine, or Religion skill (your choice).') {
                    newOptions.push(['Insight', 'Medicine', 'Religion']);
                } else if (section.desc === 'When you choose this tradition at 3rd level, you gain proficiency in the Acrobatics skill if you don\'t already have it.' +
                                            ' When you move at least 10 feet on your turn, you have advantage on the next Dexterity (Acrobatics) check you make before the start of your next turn.') {
                    fullyMatched = false;
                    newOptions.push(['Acrobatics']);
                } else if (section.desc === 'Starting at 3rd level, you gain proficiency in Animal Handling. If you already have proficiency in this skill,' +
                                            ' your proficiency bonus is doubled for any ability check you make with it.') {
                    fullyMatched = false;
                    newOptions.push(['Animal Handling', 'Animal Handling' + SkillStats.EXPERT]);
                } else if (section.desc === 'When you choose this archetype at 3rd level, you gain proficiency in the Religion skill, if you don-t already have it.') {
                    newOptions.push(['Religion']);
                } else if (section.desc === 'Starting at 1st level, you learn the *druidcraft* cantrip, and you gain proficiency in the Animal Handling skill.') {
                    fullyMatched = false;
                    newOptions.push(['Animal Handling']);
                } else if (section.desc === 'At 2nd level, you gain proficiency with longbows and shortbows, and you gain proficiency in the Stealth skill.' +
                                            ' In addition, you can still perform the somatic components of wizard spells even when you have a longbow or shortbow in one or both hands.') {
                    fullyMatched = false;
                    newOptions.push(['Stealth']);
                } else if (section.desc === 'At 2nd level, you can use an action to inscribe a small rune on a corpse. While this rune remains, the corpse can\'t become undead.' +
                                            ' You can use this feature a number of times equal to your Intelligence modifier (a minimum of once).' +
                                            ' You regain all expended uses when you finish a long rest.\n  In addition, you have proficiency in the Religion skill if you don\'t already have it,' +
                                            ' and you have advantage on Intelligence (Religion) checks made to recall lore about deities of death, burial practices, and the afterlife.') {
                    fullyMatched = false;
                    newOptions.push(['Religion']);
                }
                // expertise checks:
                else if (section.desc === 'At 3rd level, choose two of your skill proficiencies.' +
                                          ' Your proficiency bonus is doubled for any ability check you make that uses either of the chosen proficiencies.' +
                                          '\nAt 10th level, you can choose another two skill proficiencies to gain this benefit.') {
                    if (classDetails.classLevel > 2) {
                        newOptions.push([...SkillStats.getSkillsMap().keys()].map(o => o + SkillStats.EXPERT));
                        newOptions.push([...SkillStats.getSkillsMap().keys()].map(o => o + SkillStats.EXPERT));
                        if (classDetails.classLevel > 9) {
                            newOptions.push([...SkillStats.getSkillsMap().keys()].map(o => o + SkillStats.EXPERT));
                            newOptions.push([...SkillStats.getSkillsMap().keys()].map(o => o + SkillStats.EXPERT));
                        }
                    }
                } else if (section.desc === 'At 1st level, choose two of your skill proficiencies, or one of your skill proficiencies and' +
                                            ' your proficiency with thieves\' tools. Your proficiency bonus is doubled for any ability check you make that uses either of the chosen proficiencies.' +
                                            '\nAt 6th level, you can choose two more of your proficiencies (in skills or with thieves\' tools) to gain this benefit.') {
                    newOptions.push([...SkillStats.getSkillsMap().keys(), 'Thieves\' tools'].map(o => o + SkillStats.EXPERT));
                    newOptions.push([...SkillStats.getSkillsMap().keys(), 'Thieves\' tools'].map(o => o + SkillStats.EXPERT));
                    if (classDetails.classLevel > 5) {
                        newOptions.push([...SkillStats.getSkillsMap().keys()].map(o => o + SkillStats.EXPERT));
                        newOptions.push([...SkillStats.getSkillsMap().keys()].map(o => o + SkillStats.EXPERT));
                    }
                } else {
                    continue;
                }

                if (fullyMatched) {
                    section.exportSection = false;
                }
                for (const options of newOptions) {
                    skillOptions.push({ label: (classInst?.name ?? '') + ' - ' + section.name, options });
                    if (options.length === 1) {
                        skillsSelected.push(options[0]);
                    } else {
                        skillsSelected.push('');
                    }
                }

                // cantrips:
                'At 1st level, your patron gifts you with the skills necessary to defend it. You gain proficiency with shields, and you learn the *shillelagh* cantrip. *Shillelagh* counts as a warlock cantrip for you, but it doesn\'\'t count against your number of cantrips known.'
            }
        }

        /*
            "Choose one type of artisan's tools or one musical instrument",
            "Herbalism kit",
            "None",
            "Thieves' tools",
            "Three musical instruments of your choice"
        */
        if (isPrimaryClass) {
            const tools = classDetails.classInfo.prof_tools;
            if (!tools || tools === 'None') {
                return;
            }
            if (tools === 'Choose one type of artisan\'s tools or one musical instrument') {
                skillOptions.push({ label: classDetails.classInfo.name + ' (tool/inst)', options: [...SkillStats.tools, ...SkillStats.musicalInstruments] });
                skillsSelected.push('');
                return;
            }
            if (tools === 'Three musical instruments of your choice') {
                for (let i = 0; i < 3; i++) {
                    skillOptions.push({ label: classDetails.classInfo.name + ' (instr)', options: [...SkillStats.musicalInstruments] });
                    skillsSelected.push('');
                }
            }
            if (tools === 'Herbalism kit') {
                skillOptions.push({ label: classDetails.classInfo.name + ' (tool)', options: ['Herbalist kit'] });
                skillsSelected.push('Herbalist kit');
            }
            if (tools === 'Thieves\' tools') {
                skillOptions.push({ label: classDetails.classInfo.name + ' (tool)', options: ['Thieves\' tools'] });
                skillsSelected.push('Thieves\' tools');
            }
            for (const opt of [...SkillStats.tools, ...SkillStats.musicalInstruments]) {
                if (opt.toLowerCase() == tools) {
                    skillOptions.push({ label: classDetails.classInfo.name, options: [opt] });
                    skillsSelected.push(opt);
                    break;
                }
            }
        } else {
            const secondClassProficiencies = ClassService.getSecondClassSkillProficiencies(classDetails.classInfo);
            if (secondClassProficiencies?.includes('One skill of your choice')) {
                skillOptions.push({ label: classDetails.classInfo.name, options: [...SkillStats.getSkillsMap().keys()] });
                skillsSelected.push('');
            }
            if (secondClassProficiencies?.includes('one Musical Instrument of your choice')) {
                skillOptions.push({ label: classDetails.classInfo.name + ' (inst)', options: [...SkillStats.musicalInstruments] });
                skillsSelected.push('');
            }
            if (secondClassProficiencies === 'Choose one from Animal Handling, Athletics, Insight, Investigation, Nature, Perception, Stealth, and Survival') {
                skillOptions.push({ label: classDetails.classInfo.name, options: ['Animal Handling', 'Athletics', 'Insight', 'Investigation', 'Nature', 'Perception', 'Stealth', 'Survival'] });
                skillsSelected.push('');
            }
            if (secondClassProficiencies === 'Thieves\' Tools, plus choose one from Acrobatics, Athletics, Deception, Insight, Intimidation, Investigation, Perception, Performance, Persuasion, Sleight of Hand, and Stealth') {
                skillOptions.push({ label: classDetails.classInfo.name, options: ['Thieves\' tools'] });
                skillsSelected.push('Thieves\' Tools');
                skillOptions.push({ label: classDetails.classInfo.name, options: ['Acrobatics', 'Athletics', 'Deception', 'Insight', 'Intimidation', 'Investigation', 'Perception', 'Performance', 'Persuasion', 'Sleight of Hand', 'Stealth'] });
                skillsSelected.push('');
            }
            //           if (secondClassProficiencies.includes('thieves\' tools')) {
            //               skillOptions.push({ label: classInfo.name, options: ['Thieves\' tools'] });
            //               skillsSelected.push('Thieves\' tools');
            //           }
        }
    }

    static processClass(classDetails: ClassDetails | null, monsterSections: Section[]): void {
        if (classDetails?.classInfo) {
            const subtypes_name = classDetails.classInfo.subtypes_name;
            const sectionsByName = new Map<string, Section>();
            for (const section of classDetails.combinedSections) {
                sectionsByName.set(section.name, section);
            }
            for (const section of classDetails.combinedSections) {
                const optionsSelectedArray = classDetails.optionsSelectedArrayByOptionNameMap.get(section.name);
                const parent = sectionsByName.get(section.parentName || '');
                if ((section.name === subtypes_name) ||
                    (section.name + 's' === subtypes_name) ||
                    (section.name === classDetails.archetype?.name) ||
                    (section.name === 'Ability Score Improvement')) {
                    section.exportSection = false;
                } else if (section.name.toLowerCase() === 'thieves\' cant') {
                    section.exportSection = false;
                } else if (section.name.toLowerCase() === 'Druidic') {
                    section.exportSection = false;
                } else if (section.parentName == null && (section.options.length > 0) && optionsSelectedArray) {
                    // This is a parent of an option set that has a value
                    section.exportSection = false;
                    const optionsSelectedArrayLower = optionsSelectedArray.map(o => o.toLowerCase());
                    for (const child of section.children) {
                        if (optionsSelectedArrayLower.includes(child.name.trim().toLowerCase())) {
                            let desc = child.desc;
                            if (desc.startsWith('>')) {
                                desc = desc.substring(1).trim();
                            }
                            monsterSections.push(new Section(section.name + ': ' + child.name.trim(), desc));
                        }
                    }
                } else if (parent && (parent.options.length > 0) &&
                    !classDetails.optionsSelectedArrayByOptionNameMap.get(parent.name)?.includes(section.name)) {
                    // this is a child of a parent that has an option that is not set to this child.
                    section.exportSection = false;
                } else {
                    // This is fine to export, unless the ClassService.parseSkills method already this as "exportSection = false":
                    if (section.exportSection === undefined || section.exportSection === null) {
                        section.exportSection = true;
                    }
                }
                if (section.exportSection) {
                    monsterSections.push(section);
                    section.children?.forEach(c => {
                        monsterSections.push(c);
                    });
                }
            }
        }
    }

    static classData: Class[] = [];
    static getClassByName(className: string): Class | undefined {
        if (this.classData.length) {
            return this.classData.filter(classData => classData.name.toLocaleLowerCase() == className.toLocaleLowerCase())?.pop();
        }
        ClassService.fetchClasses(false).then((classData: Class[]) => ClassService.classData = classData);
    }

    static async fetchClasses(cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<Class[]> {
        try {
            const classObjs: any[] = await ApiService.getAllClasses(cacheOnly, progCallback);
            const allClasses: ClassOpen5e[] = [];
            for (const classObj of classObjs) {
                const clss: ClassOpen5e = {
                    name: '',
                    desc: '',
                    slug: '',
                    base: { name: '' },
                    subtypes_name: '',
                    hit_dice: '',
                    prof_armor: '',
                    prof_weapons: '',
                    prof_tools: '',
                    prof_saving_throws: '',
                    prof_skills: '',
                    equipment: '',
                    spellcasting_ability: '',
                    table: '',
                    sections: [],
                    archetypes: [],
                    getAsiLevels: (level: number) => [],
                    document__slug: '', document__title: '', document__license_url: '', document__url: ''
                };
                Object.assign(clss, classObj);
                allClasses.push(clss);
            }

            const classWithArchetypes = ClassService.expandArchetypes(allClasses);

            // Fix source errors:
            classWithArchetypes.forEach((pcClass) => ClassService.fixClassErrors(pcClass));
            // Add the getAsiLevels() method to each class:
            classWithArchetypes.forEach((pcClass) => ClassService.addAsiLevelToClass(pcClass));
            classWithArchetypes.forEach((pcClass) => ClassService.parseSections(pcClass));

            // update our cached copy:
            ClassService.classData = classWithArchetypes;
            return classWithArchetypes;
        } catch (error) {
            console.error("Failed to fetch classes", error);
            return [];
        }
    }
}
export default ClassService;